import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isInStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function PWAInstallPrompt({ open, onDismiss, onTriggerInstall, canInstall }) {
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);

  // If the app is already installed (standalone mode), auto-dismiss without a render side-effect
  useEffect(() => {
    if (open && isInStandalone()) {
      onDismiss();
    }
  }, [open, onDismiss]);

  if (!open || isInStandalone()) {
    return null;
  }

  const handleInstall = async () => {
    setInstalling(true);
    const accepted = await onTriggerInstall();
    if (accepted) {
      setInstalled(true);
      setTimeout(() => onDismiss(), 2000);
    } else {
      setInstalling(false);
    }
  };

  const ios = isIOS();
  const showInstallButton = !ios && canInstall;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#09090B] flex flex-col items-center justify-center px-6 py-10"
      data-testid="pwa-install-prompt"
    >
      {/* Skip */}
      <button
        onClick={onDismiss}
        data-testid="pwa-skip-btn"
        className="absolute top-5 right-5 text-[#52525B] hover:text-[#A1A1AA] transition-colors"
        aria-label="Skip"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="max-w-sm w-full flex flex-col items-center gap-6 text-center">

        {/* Icon */}
        <div className="w-24 h-24 bg-[#CCFF00]/10 border-2 border-[#CCFF00]/30 rounded-2xl flex items-center justify-center">
          <Download className="w-12 h-12 text-[#CCFF00]" />
        </div>

        {/* Heading */}
        <div className="space-y-2">
          <h1 className="text-4xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA]">
            Install the App
          </h1>
          <p className="text-[#A1A1AA] text-sm leading-relaxed">
            Add R&R Elite Fitness to your home screen for{' '}
            <span className="text-[#CCFF00] font-semibold">instant access</span>,
            push notifications, and the full mobile experience.
          </p>
        </div>

        {/* Android: one-tap install */}
        {showInstallButton && (
          <div className="w-full space-y-3">
            {installed ? (
              <div className="w-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded-sm p-4 flex items-center justify-center gap-2">
                <svg className="w-5 h-5 text-[#CCFF00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-[#CCFF00] font-bold uppercase text-sm tracking-wider">Installed!</span>
              </div>
            ) : (
              <Button
                onClick={handleInstall}
                disabled={installing}
                data-testid="pwa-install-btn"
                className="w-full h-14 bg-[#CCFF00] text-black font-black uppercase tracking-wider text-base rounded-sm hover:bg-[#B3E600] transition-all"
              >
                {installing ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Installing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    Install Now
                  </span>
                )}
              </Button>
            )}
          </div>
        )}

        {/* iOS: step-by-step guide */}
        {ios && (
          <div className="w-full space-y-3" data-testid="ios-install-guide">
            <p className="text-xs text-[#52525B] uppercase tracking-widest font-semibold mb-1">
              Follow these steps in Safari
            </p>

            {[
              {
                num: 1,
                icon: (
                  // Safari Share icon
                  <svg className="w-5 h-5 text-[#CCFF00]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                ),
                text: 'Tap the Share button at the bottom of Safari'
              },
              {
                num: 2,
                icon: (
                  // Plus in square
                  <svg className="w-5 h-5 text-[#CCFF00]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                ),
                text: "Scroll down and tap 'Add to Home Screen'"
              },
              {
                num: 3,
                icon: (
                  <svg className="w-5 h-5 text-[#CCFF00]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                ),
                text: "Tap 'Add' in the top-right corner to confirm"
              }
            ].map(step => (
              <div
                key={step.num}
                className="flex items-center gap-4 bg-[#18181B] border border-[#27272A] rounded-sm px-4 py-3 text-left"
              >
                <div className="w-8 h-8 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center flex-shrink-0">
                  {step.icon}
                </div>
                <p className="text-[#FAFAFA] text-sm leading-snug">{step.text}</p>
              </div>
            ))}

            <p className="text-xs text-[#52525B] mt-1">
              Make sure you're viewing this in Safari, not another browser.
            </p>
          </div>
        )}

        {/* Fallback: no prompt + not iOS (e.g. desktop Chrome already met criteria) */}
        {!ios && !canInstall && (
          <div className="w-full bg-[#18181B] border border-[#27272A] rounded-sm p-4 text-sm text-[#A1A1AA] leading-relaxed">
            Open this link in <span className="text-[#CCFF00] font-semibold">Chrome on Android</span> or{' '}
            <span className="text-[#CCFF00] font-semibold">Safari on iPhone</span>, then use your browser's
            "Add to Home Screen" option to install the app.
          </div>
        )}

        {/* Skip link */}
        <button
          onClick={onDismiss}
          data-testid="pwa-skip-link"
          className="text-[#52525B] hover:text-[#A1A1AA] text-sm transition-colors underline underline-offset-4 mt-2"
        >
          {ios ? "I'll do it later" : 'Skip for now'}
        </button>
      </div>
    </div>
  );
}
